import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, euro, errMsg } from "@/lib/api";
import { SUB_STATUS, frDate, useSubOffer } from "@/lib/subscription";
import { SubCampaign } from "./SubCampaign";
import { FamilleCampaign } from "./FamilleCampaign";
import { SubPerformance } from "./SubPerformance";
import { printSubscriptions } from "./printSubscriptions";
import { SubMessageButton } from "./SubMessageDialog";

const btn = "inline-flex h-10 items-center gap-2 rounded-full border border-[var(--gold)] px-4 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]";

function print(list) {
  if (!list.length) { toast("Aucune fiche à imprimer."); return; }
  if (!printSubscriptions(list)) toast.error("Autorisez les fenêtres pop-up pour imprimer.");
}

function PrintBar({ items }) {
  const active = items.filter((s) => s.status === "active");
  return (
    <div data-testid="admin-subs-print-bar" className="mt-8 flex flex-wrap items-center gap-3">
      <button data-testid="admin-subs-print-active" onClick={() => print(active)} className={btn}><Printer className="h-4 w-4" /> Imprimer les fiches actives ({active.length})</button>
      <button data-testid="admin-subs-print-all" onClick={() => print(items)} className={btn}><Printer className="h-4 w-4" /> Imprimer toutes les fiches ({items.length})</button>
    </div>
  );
}

function FirstMonthToggle({ stats, winback }) {
  const qc = useQueryClient();
  const { data } = useSubOffer();
  const on = data?.first_month_on === true;
  const toggle = async () => {
    try { await api.put("/admin/subscriptions/first-month", { enabled: !on }); toast.success(on ? "Offre 1er mois arrêtée" : "Offre 1er mois activée"); qc.invalidateQueries({ queryKey: ["sub-offer"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-first-month" className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-[var(--gold)]/60 p-4">
      <p className="text-sm"><b className="text-[var(--gold-soft)]">Offre 1er mois</b> : Duo 16,99 € et Solo 9,99 € le premier mois, une fois par adresse email (nouvelles abonnées). Statut : <b data-testid="admin-first-month-status">{on ? "active" : "arrêtée"}</b></p>
      <button data-testid="admin-first-month-toggle" onClick={toggle} className={`h-10 rounded-full px-5 text-xs font-semibold uppercase tracking-[0.18em] ${on ? "border border-[var(--line)] hover:border-red-400" : "bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]"}`}>{on ? "Arrêter l'offre" : "Activer l'offre"}</button>
      <FirstMonthStats s={stats} />
      <p data-testid="admin-winback-stats" className="w-full text-sm text-[var(--parchment)]/85"><b className="text-[var(--gold-soft)]">Offre de retour</b> (envoyée automatiquement 30 jours après l'arrêt, valable 15 jours) : <b data-testid="admin-winback-sent">{winback?.sent ?? 0}</b> envoyée{(winback?.sent ?? 0) > 1 ? "s" : ""} · <b data-testid="admin-winback-returned">{winback?.returned ?? 0}</b> revenue{(winback?.returned ?? 0) > 1 ? "s" : ""}</p>
    </div>
  );
}

function FirstMonthStats({ s }) {
  const cells = [
    ["Ont profité de l'offre", s?.taken ?? 0, "admin-fm-taken", `Duo ${s?.by_plan?.duo ?? 0} · Solo ${s?.by_plan?.solo ?? 0}`],
    ["2e mois payé", s?.second_month_paid ?? 0, "admin-fm-paid2", "restent abonnées"],
    ["En attente du 2e mois", s?.waiting ?? 0, "admin-fm-waiting", "pas encore prélevées"],
    ["Arrêtées avant le 2e mois", s?.lost_before_second ?? 0, "admin-fm-lost", "arrêt ou paiement refusé"],
    ["Taux de rétention", s?.retention == null ? "—" : `${s.retention} %`, "admin-fm-retention", "2e mois payé / mois terminés"],
  ];
  return (
    <div data-testid="admin-first-month-stats" className="grid w-full grid-cols-2 gap-3 md:grid-cols-5">
      {cells.map(([label, value, testid, hint]) => (
        <div key={testid} className="rounded-sm border border-[var(--line)] p-3">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</p>
          <p data-testid={testid} className="mt-1 font-display text-2xl">{value}</p>
          <p className="text-xs text-[var(--parchment)]/70">{hint}</p>
        </div>
      ))}
    </div>
  );
}

function ResyncButton({ s }) {
  const qc = useQueryClient();
  const run = async () => {
    try {
      const { data } = await api.post(`/admin/subscriptions/${s.id}/resync`);
      toast.success(data?.orders_created ? `${data.orders_created} commande(s) créée(s) dans l'onglet Commandes.` : `Rien à rattraper (${data?.paid_invoices ?? 0} paiement(s) Stripe, tous déjà en commande).`);
      qc.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <button data-testid={`admin-sub-resync-${s.id}`} onClick={run} className="mt-2 block text-left text-xs text-[var(--gold-soft)] underline underline-offset-4">Créer la commande manquante</button>;
}

export function SubscriptionsAdmin() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-subscriptions"], queryFn: async () => (await api.get("/admin/subscriptions")).data });
  const items = Array.isArray(data?.items) ? data.items : [];
  if (isLoading) return <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" />;
  return (
    <div data-testid="admin-subscriptions">
      <SubPerformance />
      <FirstMonthToggle stats={data?.first_month} winback={data?.winback} />
      {Array.isArray(data?.cancel_reasons) && data.cancel_reasons.length ? (
        <div data-testid="admin-cancel-reasons" className="mb-6 rounded-sm border border-[var(--line)] p-4 text-sm">
          <p className="mb-2 font-semibold text-[var(--gold-soft)]">Raisons d'arrêt</p>
          <ul className="flex flex-wrap gap-2">{data.cancel_reasons.map((r) => <li key={r.label} className="rounded-full border border-[var(--line)] px-3 py-1">{r.label} · <b>{r.count}</b></li>)}</ul>
        </div>
      ) : null}
      <FamilleCampaign />
      <SubCampaign />
      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <div className="rounded-sm border border-[var(--line)] p-4"><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">Abonnées actives</p><p data-testid="admin-subs-active" className="mt-1 font-display text-3xl">{data?.active ?? 0}</p></div>
        <div className="rounded-sm border border-[var(--line)] p-4"><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">Revenu mensuel</p><p data-testid="admin-subs-revenue" className="mt-1 font-display text-3xl">{euro(data?.monthly_revenue ?? 0)}</p></div>
      </div>
      <p className="mt-6 text-sm text-[var(--muted)]">Chaque mois payé crée automatiquement une commande « (abonnement) » dans l'onglet Commandes, avec les soins à préparer.</p>
      {!items.length ? <p data-testid="admin-subs-empty" className="mt-8 text-sm">Aucun abonnement pour le moment.</p> : (
        <>
        <PrintBar items={items} />
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-[var(--muted)]"><tr><th className="py-3">Cliente</th><th>Soins</th><th>Livraison</th><th>Statut</th><th>Prochain envoi</th><th>Colis</th><th>Fiche / email</th></tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`admin-sub-row-${s.id}`} className="border-t border-[var(--line)] align-top">
                  <td className="py-3"><b>{s.name}</b><br /><span className="text-[var(--muted)]">{s.email}<br />{s.phone}</span></td>
                  <td className="py-3"><span className="block text-xs uppercase tracking-[0.14em] text-[var(--gold-soft)]">{s.plan_label}</span>{s.plan === "reassort" ? s.items.map((i) => <span key={i.slug} className="block">{i.quantity} × {i.name}</span>) : s.products.map((p, i) => <span key={`${p.slug}-${i}`} className="block">{p.name}</span>)}</td>
                  <td className="py-3">{s.relay ? `${s.relay.name}, ${s.relay.city}` : s.shipping_label}<br /><span className="text-[var(--muted)]">{euro(s.monthly_total)} {s.plan === "reassort" && s.interval > 1 ? `tous les ${s.interval} mois` : "/ mois"}</span></td>
                  <td className="py-3 text-[var(--gold-soft)]">{SUB_STATUS[s.status] || s.status}{s.duplicate ? <span data-testid={`sub-row-duplicate-${s.id}`} className="mt-1 block w-fit rounded-full bg-red-500/20 px-2 py-0.5 text-xs font-semibold text-red-300">Doublon : 2 abonnements identiques en cours</span> : null}{s.cancel_reason ? <span data-testid={`sub-row-reason-${s.id}`} className="block text-xs text-[var(--parchment)]/80">{s.cancel_reason}{s.cancel_comment ? ` « ${s.cancel_comment} »` : ""}</span> : null}</td>
                  <td className="py-3">{s.status === "active" ? frDate(s.next_billing_at) : "—"}</td>
                  <td className="py-3">{s.deliveries}{s.status === "active" && !s.deliveries ? <ResyncButton s={s} /> : null}</td>
                  <td className="py-3"><div className="flex gap-2"><button data-testid={`admin-sub-print-${s.id}`} onClick={() => print([s])} aria-label={`Imprimer la fiche de ${s.name}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--line)] hover:border-[var(--gold)]"><Printer className="h-4 w-4" /></button><SubMessageButton s={s} /></div>{s.last_message_at ? <span data-testid={`admin-sub-last-message-${s.id}`} className="mt-1 block text-xs text-[var(--parchment)]/75">Email envoyé le {frDate(s.last_message_at)}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
